import { FieldArray, FormikProvider, useFormik } from "formik";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import DatePicker from "src/components/calendar/DatePicker";
import Dropdown from "src/components/dropdowns/Dropdown";
import Input, { ErrorMessage } from "src/components/inputs/Input";
import Modal from "src/components/modals/Modal";
import SearchList from "src/components/search-list/SearchList";
import HorizontalSeparator from "src/components/seperators/HorizontalSeperator";
import { generateRandomId } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { AddIcon } from "src/icons/AddIcon";
import { CloseIcon } from "src/icons/CloseIcon";
import {
  APPOINTMENT_TYPES,
  PAYMENT_METHOD_OPTIONS,
  RATING_OPTIONS,
} from "src/utils/constants";
import { array, number, object, string } from "yup";
import { useGetClients } from "../../client-management/api-clients";
import { useGetCoupons } from "../../coupons/api-clients";
import { useGetCourses } from "../../courses/api-clients";
import CreateCourse from "../../courses/create/CreateCourse";
import { useGetPackages } from "../../packages/api-clients";
import CreatePackage from "../../packages/create/CreatePackage";
import { useGetServices } from "../../services/api-clients";
import CreateService from "../../services/create/CreateService";
import { useGetStaff } from "../../staff-management/api-clients";
import { useGetTaxes } from "../../store-settings/api-clients";
import { useCreateBill, useGetBillData, useUpdateBill } from "../api-clients";
import ClientDetails from "./ClientDetails";
import TotalAmountDetails from "./TotalAmountDetails";
import useBillCalculation from "./useBillCalculation";

const SERVICE_SCHEMA = object().shape({
  serviceId: number().required("Required"),
  amount: number().required("Required"),
  quantity: number().required("Required"),
  staffId: number().required("Required"),
  tax: number().required("Required"),
  totalAmount: number().required("Required"),
});

const initialServiceValues = {
  id: generateRandomId(),
  type: 1,
  serviceId: null,
  staffId: null,
  quantity: null,
  amount: null,
  tax: null,
};

const FORM_FIELDS = {
  CLIENT_ID: "clientId",
  CLIENT_NAME: "clientName",
  CLIENT_GENDER: "clientGender",
  CLIENT_PHONE: "clientPhone",
  AMOUNT: "amount",
  TAX: "tax",
  PHONE: "phone",
  NAME: "name",
  BILL_DATE: "billingDate",
  DISCOUNT: "discount",
  QUANTITY: "quantity",
  RATING: "rating",
  REMARKS: "remarks",
  PAYMENT_METHOD: "paymentMethod",
  COUPON: "coupon",
  SERVICES: "services",
};

const CreateBill = ({ isInViewMode = false }: { isInViewMode?: boolean }) => {
  useSetActiveTab(TAB_NAMES.BILLING);
  const navigate = useNavigate();
  const { billId: id } = useParams();

  const [appointmentModal, setAppointmentModal] = useState(0);

  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);

  const billId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!billId, [billId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetClientLoading, data } = useGetBillData(billId);

  const billingData = useMemo(
    () =>
      ({ ...data?.data, [FORM_FIELDS.SERVICES]: data?.data?.billServices } ??
      {}),
    [data?.data]
  );

  const {
    isLoading: isUpdateLoading,
    execute: updateClient,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateBill(billId);

  const { isLoading, isSuccess, execute, error } = useCreateBill();

  const { data: clientList } = useGetClients({
    length: 10,
    page: 1,
    search: debouncedSearch,
  });

  const mappedClientList = useMemo(
    () =>
      clientList?.data?.rows?.map(
        ({ id, phone, name }: { id: number; phone: number; name: string }) => ({
          label: `${phone} (${name})`,
          value: id,
        })
      ),
    [clientList?.data?.rows]
  );

  const { data: serviceData, mutate: updateServices } = useGetServices({});

  const { data: packageData, mutate: updatePackages } = useGetPackages({});

  const { data: courseData, mutate: updateCourses } = useGetCourses({});

  const handleCloseModal = () => {
    setAppointmentModal(0);
    switch (appointmentModal) {
      case APPOINTMENT_TYPES.SERVICE:
        updateServices();
        break;
      case APPOINTMENT_TYPES.PACKAGE:
        updatePackages();
        break;
      case APPOINTMENT_TYPES.COURSE:
        updateCourses();
        break;
    }
  };

  const { data: staffData } = useGetStaff({
    length: 10,
    page: 1,
    search: "",
  });

  const { data: couponData } = useGetCoupons({
    length: 10,
    page: 1,
    search: "",
  });

  const { data: taxData } = useGetTaxes();

  const serviceTax = useMemo(() => {
    const { gst = 0, tax = 0 } =
      taxData?.data?.find(
        ({ serviceType }: { serviceType: number }) =>
          APPOINTMENT_TYPES.SERVICE === serviceType
      ) ?? {};

    return Number(gst) + Number(tax);
  }, [taxData?.data]);

  const packageTax = useMemo(() => {
    const { gst = 0, tax = 0 } =
      taxData?.data?.find(
        ({ serviceType }: { serviceType: number }) =>
          APPOINTMENT_TYPES.PACKAGE === serviceType
      ) ?? {};

    return Number(gst) + Number(tax);
  }, [taxData?.data]);

  const courseTax = useMemo(() => {
    const { gst = 0, tax = 0 } =
      taxData?.data?.find(
        ({ serviceType }: { serviceType: number }) =>
          APPOINTMENT_TYPES.COURSE === serviceType
      ) ?? {};

    return Number(gst) + Number(tax);
  }, [taxData?.data]);

  const {
    calculateTotalAmountByService,
    calculateTotalAmount,
    calculateTaxAmount,
    calculateDiscountAmountByPrice,
    getCouponDataById,
  } = useBillCalculation({ couponList: couponData });

  const getFormattedDropdownOptions = ({
    id,
    name,
  }: {
    id: number;
    name: string;
  }) => ({
    label: name,
    value: id,
  });

  const mappedServiceData = useMemo(
    () => serviceData?.data?.rows?.map(getFormattedDropdownOptions) ?? [],
    [serviceData?.data?.rows]
  );

  const mappedPackageData = useMemo(
    () => packageData?.data?.rows?.map(getFormattedDropdownOptions) ?? [],
    [packageData?.data?.rows]
  );

  const mappedCourseData = useMemo(
    () => courseData?.data?.rows?.map(getFormattedDropdownOptions) ?? [],
    [courseData?.data?.rows]
  );

  const mappedStaffData = useMemo(
    () => staffData?.data?.rows?.map(getFormattedDropdownOptions) ?? [],
    [staffData?.data?.rows]
  );

  const mappedCouponData = useMemo(
    () => couponData?.data?.rows?.map(getFormattedDropdownOptions) ?? [],
    [couponData?.data?.rows]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.CLIENT_PHONE]:
        billingData?.clients?.[FORM_FIELDS.CLIENT_PHONE] ?? "",
      [FORM_FIELDS.CLIENT_ID]: billingData?.[FORM_FIELDS.CLIENT_ID] ?? "",
      [FORM_FIELDS.SERVICES]: billingData?.[FORM_FIELDS.SERVICES] ?? [
        initialServiceValues,
      ],
      [FORM_FIELDS.BILL_DATE]: billingData?.[FORM_FIELDS.BILL_DATE] ?? "",
      [FORM_FIELDS.DISCOUNT]: billingData?.[FORM_FIELDS.DISCOUNT] ?? "",
      [FORM_FIELDS.RATING]: billingData?.[FORM_FIELDS.RATING] ?? "",
      [FORM_FIELDS.REMARKS]: billingData?.[FORM_FIELDS.REMARKS] ?? "",
      [FORM_FIELDS.COUPON]: Number(billingData?.[FORM_FIELDS.COUPON]) ?? "",
      [FORM_FIELDS.PAYMENT_METHOD]:
        billingData?.[FORM_FIELDS.PAYMENT_METHOD] ?? "",
    },
    validationSchema: object({
      [FORM_FIELDS.SERVICES]: array().of(SERVICE_SCHEMA),
      [FORM_FIELDS.BILL_DATE]: string().required("Required"),
      [FORM_FIELDS.DISCOUNT]: string().required("Required"),
      [FORM_FIELDS.RATING]: string().required("Required"),
      [FORM_FIELDS.REMARKS]: string().required("Required"),
      [FORM_FIELDS.COUPON]: string().required("Required"),
      [FORM_FIELDS.PAYMENT_METHOD]: string().required("Required"),
    }),
    onSubmit: (data) => {
      delete data?.client;
      const mappedData = data?.services?.map(
        (service: { id: string; totalAmount: string; price: string }) => {
          const { id, totalAmount, ...rest } = service;
          return { ...rest };
        }
      );
      const payload = { ...data, services: mappedData };
      if (isInEditMode) {
        updateClient(payload);
        return;
      }
      execute(payload);
    },
  });

  const { values, errors, handleSubmit, setFieldValue, touched } = formik;

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(isUpdateSuccess ? "Bill Updated" : "Bill Created.");
      navigate("/store/billing");
    }
  }, [isSuccess, isUpdateSuccess, navigate, successNotification]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification();
    }
  }, [error, errorNotification, isUpdateError]);

  const isInLoading = useMemo(
    () => isLoading || isGetClientLoading || isUpdateLoading,
    [isGetClientLoading, isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  const getServicesFieldError = (fieldName: string, index: number) => {
    return (touched[FORM_FIELDS.SERVICES] as any)?.[index]?.[fieldName]
      ? (errors[FORM_FIELDS.SERVICES] as any)?.[index]?.[fieldName]
      : undefined;
  };

  const activeClientData = useMemo(() => {
    const clientId = formik?.values?.clientId ?? {};
    return (
      clientList?.data?.rows?.find(
        ({ id }: { id: number }) => clientId === id
      ) ?? {}
    );
  }, [clientList?.data?.rows, formik?.values]);

  const calculateServiceByParam = useCallback(
    (
      serviceId: number,
      quantity: number,
      type: number,
      param: "price" | "amount"
    ) => {
      if (Number.isNaN(quantity)) return 0;

      let arr = [];
      switch (type) {
        case 2:
          arr = packageData?.data.rows;
          break;
        case 3:
          arr = courseData?.data.rows;
          break;
        default:
          arr = serviceData?.data.rows;
          break;
      }

      const arrVal = arr?.find(
        ({ id }: { id: number }) => serviceId === id
      ) ?? { price: 0, amount: 0 };

      if (param === "price") {
        return arrVal.price;
      }

      return arrVal[param] * quantity;
    },
    [courseData, packageData, serviceData]
  );

  return (
    <>
      <Modal isOpen={appointmentModal !== 0} closeModal={handleCloseModal}>
        {appointmentModal === APPOINTMENT_TYPES.SERVICE && (
          <CreateService onClose={handleCloseModal} isInModal />
        )}
        {appointmentModal === APPOINTMENT_TYPES.PACKAGE && (
          <CreatePackage onClose={handleCloseModal} isInModal />
        )}
        {appointmentModal === APPOINTMENT_TYPES.COURSE && (
          <CreateCourse onClose={handleCloseModal} isInModal />
        )}
      </Modal>
      <div className="flex flex-col gap-[20px] h-full">
        <TabHeader label="Add New Billing" showBreadcrumb />
        <FormikProvider value={formik}>
          <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
            <div className="grid grid-cols-2 gap-4 items-center">
              <SearchList
                inputClassName="!border-0 !border-b !rounded-none"
                label="Phone Number"
                value={formik.values[FORM_FIELDS.CLIENT_PHONE]}
                onSearch={(val) => {
                  setSearchText(val);
                  return mappedClientList;
                }}
                onChange={(val) => {
                  const client = clientList?.data?.rows?.find(
                    (item: any) => val === item.id
                  );
                  formik.setFieldValue(FORM_FIELDS.CLIENT_ID, client?.id);
                  formik.setFieldValue(FORM_FIELDS.CLIENT_PHONE, client?.phone);
                }}
              />

              <Button
                text={"Add new client"}
                primary
                icon={(color) => <AddIcon color={color} />}
                onClick={() => navigate("/store/clients/create")}
              />
            </div>
            <ErrorMessage text={getFieldError(FORM_FIELDS.NAME)} />
            <ClientDetails {...activeClientData} />
            <HorizontalSeparator className="!bg-[#D1D1D1]" />
            <div className="flex gap-3 justify-center">
              <Button
                text={"Add Service"}
                className="!h-9"
                onClick={() => setAppointmentModal(APPOINTMENT_TYPES.SERVICE)}
                primary
              />
              <Button
                text={"Add Package"}
                className="!h-9"
                onClick={() => setAppointmentModal(APPOINTMENT_TYPES.PACKAGE)}
                primary
              />
              <Button
                text={"Add Course"}
                className="!h-9"
                onClick={() => setAppointmentModal(APPOINTMENT_TYPES.COURSE)}
                primary
              />
            </div>
            <div className="bg-[#F8F7F7] rounded-lg p-4 overflow-x-scroll scrollbar-thin">
              <table className="border-none bg-transparent">
                <tbody>
                  <FieldArray name={FORM_FIELDS.SERVICES}>
                    {({ insert, remove, push }) => {
                      return (
                        <>
                          {values[FORM_FIELDS.SERVICES].map(
                            (item: any, index: number, arr: any) => {
                              const hasMultipleRows = arr.length > 1;

                              const { amount, quantity, tax } =
                                values[FORM_FIELDS.SERVICES]?.[index] ?? {};

                              values[FORM_FIELDS.SERVICES][index].totalAmount =
                                calculateTotalAmountByService(
                                  Number(amount),
                                  Number(quantity),
                                  Number(tax)
                                ).toString();

                              return (
                                <tr
                                  className="border-none align-top"
                                  key={item.id}
                                >
                                  <td>
                                    {APPOINTMENT_TYPES.SERVICE ===
                                      item.type && (
                                      <Dropdown
                                        label="Service"
                                        placeholder="Select Service"
                                        className={"w-full bg-white"}
                                        options={mappedServiceData}
                                        value={
                                          values[FORM_FIELDS.SERVICES]?.[
                                            index
                                          ]?.["serviceId"] as string
                                        }
                                        onChange={(val) => {
                                          setFieldValue(
                                            `${FORM_FIELDS.SERVICES}.${index}.serviceId`,
                                            val
                                          );

                                          setFieldValue(
                                            `${FORM_FIELDS.SERVICES}.${index}.amount`,
                                            calculateServiceByParam(
                                              Number(val),
                                              Number(val),
                                              values[FORM_FIELDS.SERVICES]?.[
                                                index
                                              ].type,
                                              "price"
                                            ).toString()
                                          );

                                          setFieldValue(
                                            `${FORM_FIELDS.SERVICES}.${index}.tax`,
                                            serviceTax
                                          );
                                        }}
                                        disabled={isFieldDisabled}
                                        error={getServicesFieldError(
                                          "serviceId",
                                          index
                                        )}
                                        required
                                      />
                                    )}
                                    {APPOINTMENT_TYPES.PACKAGE ===
                                      item.type && (
                                      <Dropdown
                                        label="Package"
                                        placeholder="Select Package"
                                        className={"w-full bg-white"}
                                        options={mappedPackageData}
                                        value={
                                          values[FORM_FIELDS.SERVICES]?.[
                                            index
                                          ]?.["serviceId"] as string
                                        }
                                        onChange={(val) => [
                                          setFieldValue(
                                            `${FORM_FIELDS.SERVICES}.${index}.serviceId`,
                                            val
                                          ),
                                          setFieldValue(
                                            `${FORM_FIELDS.SERVICES}.${index}.amount`,
                                            calculateServiceByParam(
                                              Number(val),
                                              Number(val),
                                              values[FORM_FIELDS.SERVICES]?.[
                                                index
                                              ].type,
                                              "price"
                                            ).toString()
                                          ),
                                          setFieldValue(
                                            `${FORM_FIELDS.SERVICES}.${index}.tax`,
                                            packageTax
                                          ),
                                        ]}
                                        disabled={isFieldDisabled}
                                        error={getServicesFieldError(
                                          "serviceId",
                                          index
                                        )}
                                        required
                                      />
                                    )}
                                    {APPOINTMENT_TYPES.COURSE === item.type && (
                                      <Dropdown
                                        label="Course"
                                        placeholder="Select Course"
                                        className={"w-full bg-white"}
                                        options={mappedCourseData}
                                        value={
                                          values[FORM_FIELDS.SERVICES]?.[
                                            index
                                          ]?.["serviceId"] as string
                                        }
                                        onChange={(val) => [
                                          setFieldValue(
                                            `${FORM_FIELDS.SERVICES}.${index}.serviceId`,
                                            val
                                          ),
                                          setFieldValue(
                                            `${FORM_FIELDS.SERVICES}.${index}.amount`,
                                            calculateServiceByParam(
                                              Number(val),
                                              Number(val),
                                              values[FORM_FIELDS.SERVICES]?.[
                                                index
                                              ].type,
                                              "price"
                                            ).toString()
                                          ),
                                          setFieldValue(
                                            `${FORM_FIELDS.SERVICES}.${index}.tax`,
                                            courseTax
                                          ),
                                        ]}
                                        disabled={isFieldDisabled}
                                        error={getServicesFieldError(
                                          "serviceId",
                                          index
                                        )}
                                        required
                                      />
                                    )}
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      label="Amount"
                                      placeholder=""
                                      name={FORM_FIELDS.AMOUNT}
                                      value={
                                        values[FORM_FIELDS.SERVICES]?.[index]?.[
                                          "amount"
                                        ] as string
                                      }
                                      error={getServicesFieldError(
                                        "amount",
                                        index
                                      )}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `${FORM_FIELDS.SERVICES}.${index}.amount`,
                                          e.target.value
                                        )
                                      }
                                      className="[&>div>div]:bg-white"
                                      disabled={isFieldDisabled}
                                      required
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      label="Quantity"
                                      placeholder=""
                                      name={FORM_FIELDS.QUANTITY}
                                      value={
                                        values[FORM_FIELDS.SERVICES]?.[index]?.[
                                          "quantity"
                                        ] as string
                                      }
                                      error={getServicesFieldError(
                                        "quantity",
                                        index
                                      )}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `${FORM_FIELDS.SERVICES}.${index}.quantity`,
                                          Number(e.target.value)
                                        )
                                      }
                                      className="[&>div>div]:bg-white"
                                      disabled={isFieldDisabled}
                                      required
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      label="Tax"
                                      placeholder=""
                                      name={FORM_FIELDS.TAX}
                                      value={
                                        values[FORM_FIELDS.SERVICES]?.[index]?.[
                                          "tax"
                                        ] as string
                                      }
                                      error={getServicesFieldError(
                                        "tax",
                                        index
                                      )}
                                      onChange={({ target: { value } }) =>
                                        setFieldValue(
                                          `${FORM_FIELDS.SERVICES}.${index}.tax`,
                                          Number(value)
                                        )
                                      }
                                      disabled
                                      required
                                    />
                                  </td>
                                  <td>
                                    <Dropdown
                                      label="Staff Assigned"
                                      placeholder="Select Staff"
                                      className={"w-full bg-white"}
                                      options={mappedStaffData}
                                      value={
                                        values[FORM_FIELDS.SERVICES]?.[index]?.[
                                          "staffId"
                                        ] as string
                                      }
                                      onChange={(val) =>
                                        setFieldValue(
                                          `${FORM_FIELDS.SERVICES}.${index}.staffId`,
                                          val
                                        )
                                      }
                                      disabled={isFieldDisabled}
                                      error={getServicesFieldError(
                                        "staffId",
                                        index
                                      )}
                                      required
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      label="Total Amount"
                                      placeholder=""
                                      name={FORM_FIELDS.SERVICES}
                                      value={
                                        values[FORM_FIELDS.SERVICES]?.[index]?.[
                                          "totalAmount"
                                        ] as string
                                      }
                                      error={getServicesFieldError(
                                        "totalAmount",
                                        index
                                      )}
                                      onChange={() => {}}
                                      disabled
                                      required
                                    />
                                  </td>
                                  <td className="flex items-end h-full">
                                    {hasMultipleRows && (
                                      <div
                                        className="flex h-full justify-center items-center top-4 mt-[38px] cursor-pointer"
                                        onClick={() => remove(index)}
                                      >
                                        <CloseIcon color="#B22234" />
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            }
                          )}
                          <tr className="col-span-3">
                            <td colSpan={4}>
                              {!isInViewMode && (
                                <div className="flex gap-3">
                                  <Button
                                    text={"+ Service"}
                                    className="mt-4 h-[40px]"
                                    onClick={() => push(initialServiceValues)}
                                    ghost
                                  />
                                  <Button
                                    text={"+ Package"}
                                    className="mt-4 h-[40px]"
                                    onClick={() =>
                                      push({
                                        ...initialServiceValues,
                                        type: APPOINTMENT_TYPES.PACKAGE,
                                        id: generateRandomId(),
                                      })
                                    }
                                    ghost
                                  />
                                  <Button
                                    text={"+ Course"}
                                    className="mt-4 h-[40px]"
                                    onClick={() =>
                                      push({
                                        ...initialServiceValues,
                                        type: APPOINTMENT_TYPES.COURSE,
                                        id: generateRandomId(),
                                      })
                                    }
                                    ghost
                                  />
                                </div>
                              )}
                            </td>
                          </tr>
                        </>
                      );
                    }}
                  </FieldArray>
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
              <DatePicker
                label="Bill Date"
                value={values[FORM_FIELDS.BILL_DATE]}
                onChange={(value) => {
                  return setFieldValue(
                    FORM_FIELDS.BILL_DATE,
                    `${moment(value?.toString()).format(
                      "YYYY-MM-DD"
                    )}T${moment().format("HH:mm:ss")}Z`
                  );
                }}
                disabled={isFieldDisabled}
                error={getFieldError(FORM_FIELDS.BILL_DATE)}
                required
              />
              <Input
                type="number"
                label="Discount"
                placeholder="Discount"
                name={FORM_FIELDS.DISCOUNT}
                value={values[FORM_FIELDS.DISCOUNT] as string}
                error={getFieldError(FORM_FIELDS.DISCOUNT)}
                onChange={(e) =>
                  setFieldValue(FORM_FIELDS.DISCOUNT, Number(e.target.value))
                }
                disabled={isFieldDisabled}
                rightIcon={() => (
                  <div className="h-full border-l-[1px] flex justify-center items-center p-2 absolute right-0 text-gray-suva text-14px">
                    %
                  </div>
                )}
                required
              />

              <Dropdown
                label="Rating"
                placeholder="Select Rating"
                className={"w-full"}
                options={RATING_OPTIONS}
                value={values[FORM_FIELDS.RATING] as string}
                onChange={(val) => setFieldValue(FORM_FIELDS.RATING, val)}
                error={getFieldError(FORM_FIELDS.RATING)}
                disabled={isFieldDisabled}
                required
              />

              <Input
                label="Remarks"
                placeholder="Remarks"
                name={FORM_FIELDS.REMARKS}
                value={values[FORM_FIELDS.REMARKS] as string}
                error={getFieldError(FORM_FIELDS.REMARKS)}
                onChange={(e) =>
                  setFieldValue(FORM_FIELDS.REMARKS, e.target.value)
                }
                disabled={isFieldDisabled}
                required
              />

              <Dropdown
                label="Coupon"
                placeholder="Coupon"
                className={"w-full bg-white"}
                options={mappedCouponData}
                value={values[FORM_FIELDS.COUPON] as string}
                onChange={(val) => setFieldValue(FORM_FIELDS.COUPON, val)}
                disabled={isFieldDisabled}
                error={getFieldError(FORM_FIELDS.COUPON)}
              />

              <Dropdown
                label="Payment Methods"
                placeholder="Select Method"
                className={"w-full"}
                options={PAYMENT_METHOD_OPTIONS}
                value={values[FORM_FIELDS.PAYMENT_METHOD] as string}
                onChange={(val) =>
                  setFieldValue(FORM_FIELDS.PAYMENT_METHOD, val)
                }
                disabled={isFieldDisabled}
                error={getFieldError(FORM_FIELDS.PAYMENT_METHOD)}
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-[40px] mt-4">
              {/* <ClientHistory className="col-span-2" /> */}
              <TotalAmountDetails
                subtotal={calculateTotalAmount(values[FORM_FIELDS.SERVICES])}
                gst={calculateTaxAmount(values[FORM_FIELDS.SERVICES])}
                discount={calculateDiscountAmountByPrice(
                  calculateTotalAmount(values[FORM_FIELDS.SERVICES]),
                  Number(values[FORM_FIELDS.DISCOUNT] ?? 0)
                )}
                coupon={calculateDiscountAmountByPrice(
                  calculateTotalAmount(values[FORM_FIELDS.SERVICES]),
                  getCouponDataById(values[FORM_FIELDS.COUPON])
                )}
                showDiscount
                showCoupon
              />
            </div>
            <Button
              text={isLoading ? "Loading" : "Submit"}
              onClick={handleSubmit}
              primary
              className="h-[40px] px-10"
              isDisabled={isFieldDisabled}
            />
          </div>
        </FormikProvider>
      </div>
    </>
  );
};

export default CreateBill;
